import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import { useCore } from '../hooks/useCore';
import { usePools } from '../hooks/usePools';
import HeroStats from '../components/HeroStats';
import Loader from '../components/Loader';
import {
  PACKAGE_LABELS, PACKAGE_NAMES, PACKAGE_COLORS, POOL_LABELS,
  formatUSDTRaw,
} from '../utils/helpers';
import '../styles/dashboard.css';

export default function Dashboard({ onNotify }) {
  const { account, userId, provider, refreshUserId } = useWeb3();
  const { getUserInfo, getIncomeBreakdown, getTotalUsers, getUSDTBalance, getDirectReferrals, getCoreRead } = useCore();
  const { getUserTotalClaimable, claimAll } = usePools();
  const { register, registerMultiple, activatePackage } = useCore();

  const [userInfo, setUserInfo] = useState(null);
  const [income, setIncome] = useState(null);
  const [poolClaimable, setPoolClaimable] = useState(null);
  const [totalUsers, setTotalUsers] = useState('0');
  const [usdtBalance, setUsdtBalance] = useState(null);
  const [directCount, setDirectCount] = useState(0);
  const [teamCount, setTeamCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [referrerInput, setReferrerInput] = useState('');
  const [selectedPkg, setSelectedPkg] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [totalPoolEarned, setTotalPoolEarned] = useState(ethers.BigNumber.from(0));
  const [allEvents, setAllEvents] = useState([]);
  const [actTotal, setActTotal] = useState(0);
  const [actPage, setActPage] = useState(0);
  const [showAllAct, setShowAllAct] = useState(false);
  const [actLoading, setActLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferrerInput(ref);
  }, []);

  useEffect(() => {
    if (provider) loadData();
    // eslint-disable-next-line
  }, [userId, provider, account]);

  async function loadData() {
    setLoading(true);
    try {
      const tu = await getTotalUsers();
      setTotalUsers(tu);
      if (userId) {
        const [info, inc, bal, directs, claimable] = await Promise.all([
          getUserInfo(userId),
          getIncomeBreakdown(userId),
          getUSDTBalance(),
          getDirectReferrals(userId),
          getUserTotalClaimable(userId),
        ]);
        setUserInfo(info);
        setIncome(inc);
        setUsdtBalance(bal);
        setDirectCount(directs.length);
        setPoolClaimable(claimable);

        let team = directs.length;
        try {
          const l2Promises = directs.slice(0, 50).map(d => getDirectReferrals(d));
          const l2Results = await Promise.all(l2Promises);
          l2Results.forEach(r => { team += r.length; });
        } catch {}
        setTeamCount(team);

        loadActivities();
      } else {
        const bal = await getUSDTBalance();
        setUsdtBalance(bal);
      }
    } catch {}
    setLoading(false);
  }

  // Activity type mapping: 1=Package, 2=Board, 3=Direct, 4=Level, 5=PoolClaim, 6=RoyaltyClaim
  const ACT_TYPE_MAP = {
    1: { type: 'package', icon: 'pkg', colorClass: 'act-pkg' },
    2: { type: 'board', icon: 'board', colorClass: 'act-board' },
    3: { type: 'direct', icon: 'direct', colorClass: 'act-direct' },
    4: { type: 'level', icon: 'level', colorClass: 'act-level' },
    5: { type: 'pool', icon: 'pool', colorClass: 'act-pool' },
    6: { type: 'royalty', icon: 'pool', colorClass: 'act-pool' },
  };

  function formatActivity(act) {
    const actType = act.activityType;
    const meta = ACT_TYPE_MAP[actType] || { type: 'unknown', icon: 'pkg', colorClass: 'act-pkg' };
    const relId = act.relatedId.toString();
    const amount = act.amount;
    const net = act.netAmount;
    const ts = act.timestamp.toString();

    let label = '';
    let value = null;

    switch (actType) {
      case 1: // Package Activation
        const pkgId = parseInt(relId);
        label = `Activated ${PACKAGE_NAMES[pkgId] || 'Package'} (${PACKAGE_LABELS[pkgId] || ''})`;
        break;
      case 2: // Board Income
        label = `Board income from #${relId}`;
        value = `+$${formatUSDTRaw(amount)}`;
        break;
      case 3: // Direct Income
        label = `Direct income from #${relId}`;
        value = `+$${formatUSDTRaw(amount)}`;
        break;
      case 4: // Level Income
        label = `Level income from #${relId}`;
        value = `+$${formatUSDTRaw(amount)}`;
        break;
      case 5: // Pool Claim
        const poolIdx = parseInt(relId);
        label = `${POOL_LABELS[poolIdx] || 'Daily Share'} claimed`;
        value = `+$${formatUSDTRaw(amount)}`;
        break;
      case 6: // Royalty Claim
        label = relId === '0' ? 'Weekly Royalty claimed' : 'Monthly Royalty claimed';
        value = `+$${formatUSDTRaw(amount)}`;
        break;
      default:
        label = 'Activity';
    }

    return { ...meta, label, value, timestamp: ts };
  }

  async function loadActivities() {
    if (!provider || !userId) return;
    setActLoading(true);
    try {
      const core = getCoreRead();
      if (!core) { setActLoading(false); return; }

      // Fetch all activities from on-chain (max 100, newest first, paginated)
      const result = await core.getUserActivities(userId, 0, 100);
      const activities = result.activities;
      const total = result.total.toNumber();
      setActTotal(total);

      let poolEarnedTotal = ethers.BigNumber.from(0);
      const events = [];

      for (let i = 0; i < activities.length; i++) {
        const act = activities[i];
        if (act.timestamp.toNumber() === 0) continue; // skip empty slots
        const formatted = formatActivity(act);
        events.push(formatted);

        // Accumulate pool/royalty earned for income chart
        if (act.activityType === 5 || act.activityType === 6) {
          poolEarnedTotal = poolEarnedTotal.add(act.amount);
        }
      }

      // Add pending claimable to total pool earned
      if (poolClaimable?.grandTotal) {
        poolEarnedTotal = poolEarnedTotal.add(poolClaimable.grandTotal);
      }
      setTotalPoolEarned(poolEarnedTotal);

      setAllEvents(events);
      setActPage(0);
    } catch (err) {
      console.warn('Failed to load activities:', err);
    }
    setActLoading(false);
  }

  function copyReferralLink() {
    const link = `${window.location.origin}?ref=${userId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onNotify) onNotify('Referral link copied!', 'success');
    });
  }

  async function handleRegister() {
    if (actionLoading) return;
    setActionLoading(true);
    const refId = referrerInput.trim() || '0';
    let result;
    if (selectedPkg > 0) {
      result = await registerMultiple(refId, selectedPkg, onNotify);
    } else {
      result = await register(refId, onNotify);
    }
    if (result.success) {
      onNotify('Registration successful!', 'success');
      // Refresh userId so user moves to dashboard
      setTimeout(async () => {
        await refreshUserId();
        loadData();
      }, 2000);
    } else {
      onNotify(result.error, 'error');
    }
    setActionLoading(false);
  }

  async function handleActivate(pkgId) {
    if (actionLoading) return;
    setActionLoading(true);
    const result = await activatePackage(pkgId, onNotify);
    if (result.success) {
      onNotify(`${PACKAGE_NAMES[pkgId]} package activated!`, 'success');
      setTimeout(loadData, 2000);
    } else {
      onNotify(result.error, 'error');
    }
    setActionLoading(false);
  }

  async function handleClaimAll() {
    if (claimLoading) return;
    setClaimLoading(true);
    const result = await claimAll();
    if (result.success) {
      onNotify('All rewards claimed!', 'success');
      setTimeout(loadData, 2000);
    } else {
      onNotify(result.error, 'error');
    }
    setClaimLoading(false);
  }

  function getCurrentPackage() {
    if (!userInfo?.packageActive) return { name: 'None', idx: -1 };
    for (let i = 6; i >= 0; i--) {
      if (userInfo.packageActive[i]) return { name: PACKAGE_NAMES[i], label: PACKAGE_LABELS[i], idx: i, color: PACKAGE_COLORS[i] };
    }
    return { name: 'None', idx: -1, color: '#666' };
  }

  function getIncomeChart() {
    if (!income) return [];
    const board = parseFloat(formatUSDTRaw(income.boardIncome));
    const direct = parseFloat(formatUSDTRaw(income.directIncome));
    const level = parseFloat(formatUSDTRaw(income.levelIncome));
    // Use totalPoolEarned (claimed + pending) instead of just pending claimable
    const pool = parseFloat(formatUSDTRaw(totalPoolEarned));
    const total = board + direct + level + pool;
    if (total === 0) return [
      { label: 'Board', value: 0, pct: 0, color: '#f97316' },
      { label: 'Direct', value: 0, pct: 0, color: '#ec4899' },
      { label: 'Level', value: 0, pct: 0, color: '#8b5cf6' },
      { label: 'Daily Share', value: 0, pct: 0, color: '#34d399' },
    ];
    return [
      { label: 'Board', value: board, pct: (board / total * 100).toFixed(0), color: '#f97316' },
      { label: 'Direct', value: direct, pct: (direct / total * 100).toFixed(0), color: '#ec4899' },
      { label: 'Level', value: level, pct: (level / total * 100).toFixed(0), color: '#8b5cf6' },
      { label: 'Daily Share', value: pool, pct: (pool / total * 100).toFixed(0), color: '#34d399' },
    ];
  }

  // Pagination logic
  const PER_PAGE_SMALL = 5;
  const PER_PAGE_FULL = 10;
  const perPage = showAllAct ? PER_PAGE_FULL : PER_PAGE_SMALL;
  const pagedEvents = showAllAct
    ? allEvents.slice(actPage * PER_PAGE_FULL, (actPage + 1) * PER_PAGE_FULL)
    : allEvents.slice(0, PER_PAGE_SMALL);
  const totalPages = Math.ceil(allEvents.length / PER_PAGE_FULL);

  // =================== WELCOME PAGE ===================
  if (!account) {
    return (
      <div className="page-container">
        <div className="welcome-page">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
          <div className="welcome-content">
            <div className="welcome-logo-wrap">
              <img src="/logo.png" alt="CLUSTER" className="welcome-logo" />
              <div className="welcome-logo-glow"></div>
            </div>
            <h1 className="welcome-title">Welcome to CLUSTER</h1>
            <p className="welcome-subtitle">The next-generation decentralized income ecosystem. Build your network, earn multiple income streams, and grow with the community.</p>

            <div className="welcome-features">
              <div className="feature-card">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                </div>
                <h3>X6 Matrix</h3>
                <p>Auto-reinvesting board system with 50% allocation for maximum earnings</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></svg>
                </div>
                <h3>25% Direct Referral</h3>
                <p>Earn instantly when your direct partners activate packages</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20V10M18 20V4M6 20v-4"/></svg>
                </div>
                <h3>15 Level Deep</h3>
                <p>Earn 1% from each of 15 levels deep in your network tree</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                </div>
                <h3>Daily Share & Royalty</h3>
                <p>Passive daily share pools plus weekly and monthly royalty income</p>
              </div>
            </div>

            <div className="welcome-highlights">
              <div className="highlight-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
                <span>100% On-Chain & Transparent</span>
              </div>
              <div className="highlight-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
                <span>Start with just $1 USDT</span>
              </div>
              <div className="highlight-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
                <span>7 Upgrade Packages Available</span>
              </div>
              <div className="highlight-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
                <span>Instant USDT Payouts to Wallet</span>
              </div>
              <div className="highlight-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
                <span>Built on opBNB for Low Gas Fees</span>
              </div>
            </div>

            <p className="welcome-cta-text">Connect your wallet to get started</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="page-container"><Loader text="Loading dashboard..." /></div>;

  // =================== REGISTRATION ===================
  if (!userId) {
    return (
      <div className="page-container">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="register-card animate-in">
          <div className="register-logo-wrap">
            <img src="/logo.png" alt="CLUSTER" className="register-logo" />
          </div>
          <h2>Join CLUSTER</h2>
          <p className="register-subtitle">Start your decentralized income journey</p>
          <div className="form-group">
            <label>Referrer ID (optional)</label>
            <input type="text" placeholder="Enter referrer ID" value={referrerInput} onChange={e => setReferrerInput(e.target.value)} className="form-input" />
          </div>
          <div className="form-group">
            <label>Select Package</label>
            <div className="pkg-select-grid">
              {PACKAGE_NAMES.map((name, i) => (
                <button key={i} className={`pkg-select-btn ${selectedPkg >= i ? 'selected' : ''}`} onClick={() => setSelectedPkg(i)}>
                  <span className="pkg-name">{name}</span>
                  <span className="pkg-price">{PACKAGE_LABELS[i]}</span>
                </button>
              ))}
            </div>
            <div className="register-cost">
              <span className="cost-label">Total Cost</span>
              <span className="cost-value">${[1,5,10,25,50,100,200].slice(0,selectedPkg+1).reduce((a,b)=>a+b,0)} USDT</span>
            </div>
          </div>
          <button className="btn-gradient btn-full btn-glow" onClick={handleRegister} disabled={actionLoading}>
            {actionLoading ? <span className="btn-loading"><span className="btn-spinner"></span>Processing...</span> : 'Register Now'}
          </button>
          {usdtBalance && (
            <div className="wallet-balance-info">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg>
              USDT Balance: <strong>${formatUSDTRaw(usdtBalance)}</strong>
            </div>
          )}
        </div>
      </div>
    );
  }

  // =================== REGISTERED DASHBOARD ===================
  const currentPkg = getCurrentPackage();
  const chartData = getIncomeChart();
  const grandTotal = poolClaimable?.grandTotal;
  const hasClaimable = grandTotal && grandTotal.toString() !== '0';

  return (
    <div className="page-container">

      {/* ===== USER CARD ===== */}
      <div className="user-card glass-card animate-in">
        <div className="user-card-top">
          <div className="user-avatar">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div className="user-info">
            <div className="user-id-row">
              <span className="user-id-label">ID</span>
              <span className="user-id-value">{userId}</span>
            </div>
            <div className="user-upline-row">
              <span className="user-upline-label">Upline</span>
              <span className="user-upline-value">#{userInfo?.referrerId || 'N/A'}</span>
            </div>
          </div>
          <div className="user-rank-badge" style={{ borderColor: currentPkg.color }}>
            <span className="rank-name" style={{ color: currentPkg.color }}>{currentPkg.name}</span>
            <span className="rank-price">{currentPkg.label}</span>
          </div>
        </div>
        <div className="pkg-progress">
          {PACKAGE_NAMES.map((name, i) => (
            <div key={i} className={`pkg-dot ${userInfo?.packageActive?.[i] ? 'active' : ''}`} title={name} style={userInfo?.packageActive?.[i] ? { background: PACKAGE_COLORS[i] } : {}}>
              <span className="pkg-dot-label">{name.slice(0,1)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ===== INCOME HERO ===== */}
      <HeroStats
        totalIncome={userInfo?.totalIncomeEarned}
        boardIncome={income?.boardIncome}
        directIncome={income?.directIncome}
        levelIncome={income?.levelIncome}
        usdtBalance={usdtBalance}
      />

      {/* ===== CLAIMABLE INCOME ===== */}
      {hasClaimable && (
        <div className="claimable-card glass-card animate-in" style={{animationDelay:'0.05s'}}>
          <div className="claimable-info">
            <span className="claimable-label">Available to Claim</span>
            <span className="claimable-value">${formatUSDTRaw(grandTotal)} USDT</span>
          </div>
          <button className="btn-gradient btn-sm" onClick={handleClaimAll} disabled={claimLoading}>
            {claimLoading ? 'Claiming...' : 'Claim All'}
          </button>
        </div>
      )}

      {/* ===== REFERRAL LINK ===== */}
      <div className="referral-card glass-card animate-in" style={{animationDelay:'0.05s'}}>
        <div className="referral-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
          <span>Referral Link</span>
        </div>
        <div className="referral-link-box">
          <span className="referral-link">{window.location.origin}?ref={userId}</span>
          <button className="btn-copy" onClick={copyReferralLink}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* ===== TEAM STATS ===== */}
      <div className="team-row animate-in" style={{animationDelay:'0.1s'}}>
        <div className="team-card glass-card">
          <div className="team-icon" style={{background:'rgba(236,72,153,0.12)'}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></svg>
          </div>
          <span className="team-value">{directCount}</span>
          <span className="team-label">Direct Partners</span>
        </div>
        <div className="team-card glass-card">
          <div className="team-icon" style={{background:'rgba(139,92,246,0.12)'}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
          </div>
          <span className="team-value">{teamCount}</span>
          <span className="team-label">Total Team</span>
        </div>
      </div>

      {/* ===== INCOME CHART ===== */}
      <div className="section-card glass-card animate-in" style={{animationDelay:'0.15s'}}>
        <h3 className="section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>
          Income Breakdown
        </h3>
        <div className="income-chart">
          {chartData.map((item, i) => (
            <div key={i} className="chart-row">
              <div className="chart-label-row">
                <div className="chart-dot" style={{background: item.color}}></div>
                <span className="chart-name">{item.label}</span>
                <span className="chart-amount">${item.value.toFixed(2)}</span>
              </div>
              <div className="chart-bar-bg">
                <div className="chart-bar-fill" style={{width: `${Math.max(item.pct, 2)}%`, background: item.color}}></div>
              </div>
              <span className="chart-pct">{item.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* ===== PACKAGES - New innovative design ===== */}
      <div className="section-card glass-card animate-in" style={{animationDelay:'0.2s'}}>
        <h3 className="section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
          Packages
        </h3>
        <div className="pkg-timeline">
          {PACKAGE_NAMES.map((name, i) => {
            const active = userInfo?.packageActive?.[i];
            const prevActive = i === 0 || userInfo?.packageActive?.[i - 1];
            const canActivate = !active && prevActive;
            const isNext = canActivate;
            return (
              <div key={i} className={`pkg-timeline-item ${active ? 'active' : ''} ${isNext ? 'next' : ''}`}>
                <div className="pkg-timeline-line" style={{background: active ? PACKAGE_COLORS[i] : 'var(--border)'}}></div>
                <div className="pkg-timeline-dot" style={active ? {background: PACKAGE_COLORS[i], boxShadow: `0 0 12px ${PACKAGE_COLORS[i]}44`} : {}}>
                  {active && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                </div>
                <div className="pkg-timeline-content">
                  <div className="pkg-timeline-header">
                    <span className="pkg-t-name">{name}</span>
                    <span className="pkg-t-price" style={active ? {color: PACKAGE_COLORS[i]} : {}}>{PACKAGE_LABELS[i]}</span>
                  </div>
                  {active && <span className="pkg-t-status active">Active</span>}
                  {!active && !isNext && <span className="pkg-t-status locked">Locked</span>}
                  {isNext && (
                    <button className="btn-sm btn-gradient pkg-activate-btn" onClick={() => handleActivate(i)} disabled={actionLoading}>
                      Activate
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== REPORTS (Activities) ===== */}
      <div className="section-card glass-card animate-in" style={{animationDelay:'0.25s'}}>
        <h3 className="section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
          Reports
        </h3>

        {pagedEvents.length > 0 ? (
          <div className="activities-list">
            {pagedEvents.map((act, i) => (
              <div key={`${act.timestamp}-${i}`} className={`activity-item ${act.colorClass || ''}`}>
                <div className={`activity-icon ${act.colorClass || ''}`}>
                  {act.icon === 'pkg' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                  )}
                  {act.icon === 'direct' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></svg>
                  )}
                  {act.icon === 'level' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20V10M18 20V4M6 20v-4"/></svg>
                  )}
                  {act.icon === 'board' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  )}
                  {act.icon === 'pool' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                  )}
                </div>
                <div className="activity-info">
                  <span className="activity-label">{act.label}</span>
                  <span className="activity-time">{act.timestamp && act.timestamp !== '0' ? new Date(parseInt(act.timestamp) * 1000).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                </div>
                {act.value && <span className={`activity-value ${act.colorClass || ''}`}>{act.value}</span>}
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-text">{actLoading ? 'Loading...' : 'No recent reports'}</p>
        )}

        {/* View All button (only when not expanded and more than 5 events) */}
        {!showAllAct && allEvents.length > PER_PAGE_SMALL && (
          <button className="btn-view-all" onClick={() => { setShowAllAct(true); setActPage(0); }}>
            View All ({allEvents.length})
          </button>
        )}

        {/* Pagination (only in expanded mode) */}
        {showAllAct && totalPages > 0 && (
          <div className="pagination">
            <button className="page-btn" onClick={() => setActPage(p => Math.max(0, p - 1))} disabled={actPage === 0}>Prev</button>
            <span className="page-info">Page {actPage + 1} / {totalPages}</span>
            <button className="page-btn" onClick={() => setActPage(p => Math.min(totalPages - 1, p + 1))} disabled={actPage >= totalPages - 1}>Next</button>
          </div>
        )}
      </div>

    </div>
  );
}
